import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ProfessionalOption } from '@/components/public-profile/professional-pills';
import { MemberAvatar, getFirstName } from '@/components/team/member-avatar';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useThemedHex, useIconChip } from '@/hooks/use-theme-colors';

const CARD_WIDTH = 108;
const AVATAR = 64;

function Card({
  selected,
  label,
  note,
  brandColor,
  onPress,
  onOpenProfile,
  children,
}: {
  selected: boolean;
  label: string;
  note?: string;
  brandColor: string;
  onPress: () => void;
  /** Abre o perfil da pessoa; ausente no card "qualquer". */
  onOpenProfile?: () => void;
  /** A foto (ou o ícone de "qualquer"). */
  children: React.ReactNode;
}) {
  const th = useThemedHex();
  return (
    <PressableScale
      haptic
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      className="items-center gap-2 rounded-2xl border bg-card px-2 pb-3 pt-3"
      style={[
        { width: CARD_WIDTH, borderCurve: 'continuous', borderColor: th('#E5E5E5') },
        selected && {
          borderColor: brandColor,
          backgroundColor: `${brandColor}0D`,
          boxShadow: `0 4px 14px ${brandColor}26`,
        },
      ]}
    >
      <View>
        {/* Anel da cor da marca em volta da foto de quem está escolhido. */}
        <View
          className="items-center justify-center rounded-full"
          style={{
            padding: 2,
            borderWidth: 2,
            borderRadius: (AVATAR + 8) / 2,
            borderColor: selected ? brandColor : 'transparent',
          }}
        >
          {children}
        </View>
        {selected && (
          <View
            className="absolute -bottom-0.5 -right-0.5 h-5 w-5 items-center justify-center rounded-full border-2 border-card"
            style={{ backgroundColor: brandColor }}
          >
            <Ionicons name="checkmark" size={11} color="#fff" />
          </View>
        )}
      </View>

      <View className="items-center gap-0.5">
        <ThemedText
          numberOfLines={1}
          className="font-bold text-[13px] capitalize"
          style={{ color: selected ? brandColor : th('#171717') }}
        >
          {label}
        </ThemedText>
        {!!note && (
          <ThemedText numberOfLines={1} className="text-[11px] text-neutral-400">
            {note}
          </ThemedText>
        )}
        {/* Sempre visível: o perfil se abre sem precisar escolher a pessoa. */}
        {!!onOpenProfile && (
          <Pressable
            onPress={onOpenProfile}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={`Ver perfil de ${label}`}
            className="mt-1 flex-row items-center gap-0.5"
          >
            <ThemedText className="font-semibold text-[11px]" style={{ color: brandColor }}>
              ver perfil
            </ThemedText>
            <Ionicons name="chevron-forward" size={10} color={brandColor} />
          </Pressable>
        )}
      </View>
    </PressableScale>
  );
}

/**
 * "Com quem?" na página pública: um card por profissional, com a foto grande o
 * bastante para reconhecer a pessoa. As pills antigas mostravam um avatar de
 * 24px — quem escolhe o barbeiro escolhe pela cara dele.
 */
export function ProfessionalCards({
  options,
  selectedId,
  onSelect,
  brandColor,
  allLabel,
  /** Legenda abaixo do nome — ex.: "livre às 14:00". */
  noteFor,
  onOpenProfile,
}: {
  options: ProfessionalOption[];
  selectedId: string | null;
  onSelect: (providerId: string | null) => void;
  brandColor: string;
  /** Opção "qualquer profissional"; ausente, só as pessoas. */
  allLabel?: string;
  noteFor?: (option: ProfessionalOption) => string | undefined;
  /** Dado, cada card ganha um "ver perfil" que abre a página da pessoa. */
  onOpenProfile?: (providerId: string) => void;
}) {
  const chip = useIconChip();
  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ gap: 10, paddingVertical: 4, paddingRight: 4 }}
    >
      {!!allLabel && (
        <Card
          selected={selectedId === null}
          label={allLabel}
          note="quem estiver livre"
          brandColor={brandColor}
          onPress={() => onSelect(null)}
        >
          <View
            className="items-center justify-center rounded-full"
            style={{
              width: AVATAR,
              height: AVATAR,
              backgroundColor: chip(brandColor, '1A').bg,
            }}
          >
            <Ionicons name="people" size={28} color={chip(brandColor, '1A').fg} />
          </View>
        </Card>
      )}
      {options.map((option) => (
        <Card
          key={option.providerId}
          selected={selectedId === option.providerId}
          label={getFirstName(option.name)}
          note={noteFor?.(option)}
          brandColor={brandColor}
          onPress={() => onSelect(option.providerId)}
          onOpenProfile={onOpenProfile ? () => onOpenProfile(option.providerId) : undefined}
        >
          <MemberAvatar name={option.name} image={option.image} color={brandColor} size={AVATAR} />
        </Card>
      ))}
    </ScrollView>
  );
}
